/**
 * Generic list screen: header, optional create form, and the three states
 * every list needs — loading, failed, empty.
 *
 * The empty and error states are the point of this existing. Hand-written
 * lists in this codebase reliably render a skeleton forever on error, or an
 * empty table that looks identical to "no data", because those branches are
 * the easy ones to skip. Centralising them means a new screen gets them for
 * free and cannot forget.
 *
 * Create fields are declared, not hand-wired, so a page that only lists is a
 * few lines and a page that also creates is a few more.
 *
 * Its own words (Cancel, Save, the failed-load title, the API's error) come
 * from the translation layer; the title, columns and fields a page passes in
 * are that page's to translate.
 */
import { useRef, useState, type ReactNode } from "react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { post, useApiList, useApiMutation } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";

export interface CreateField {
  field: string;
  label: string;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  type?: "text" | "number" | "date" | "email" | "url";
  /** A picker: each option a value shown as itself, or a value and a label. */
  options?: Array<string | { value: string; label: string }>;
  /**
   * A first option with the value "" and this label, chosen when the form
   * opens without an `initial`: nothing is picked for the person.
   */
  emptyOption?: string;
  /** Default applied when the form opens. */
  initial?: string;
}

const optionValue = (option: string | { value: string }): string =>
  typeof option === "string" ? option : option.value;
const optionLabel = (option: string | { label: string }): string =>
  typeof option === "string" ? option : option.label;

export interface ResourceListPageProps<TRow extends object> {
  title: string;
  subtitle: string;
  breadcrumb?: string[];
  /** Canonical API path for both the list and the create. */
  path: string;
  /** Create path when it differs from the list path (e.g. `/planning/mrp/run`). */
  createPath?: string;
  queryKey: string[];
  columns: Array<{
    key: string;
    label: string;
    numeric?: boolean;
    render?: (row: TRow) => ReactNode;
  }>;
  emptyTitle: string;
  emptyMessage: string;
  createLabel?: string;
  createFields?: CreateField[];
  /** Rendered above the table — stat cards, filters, explanations. */
  children?: ReactNode;
  /** Maps form state to the request body when the API expects a different shape. */
  toBody?: (form: Record<string, string>) => Record<string, unknown>;
  /**
   * The create route is @Idempotent and refuses a request without a key
   * (NUM-P0). The key is made when the dialog opens and reused until the
   * create succeeds, so a retried Save is the same request.
   */
  idempotent?: boolean;
}

export function ResourceListPage<TRow extends object>({
  title,
  subtitle,
  breadcrumb,
  path,
  createPath,
  queryKey,
  columns,
  emptyTitle,
  emptyMessage,
  createLabel,
  createFields,
  children,
  toBody,
  idempotent,
}: ResourceListPageProps<TRow>) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useApiList<TRow>(queryKey, path);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const idempotencyKey = useRef<string | null>(null);

  const canCreate = Boolean(
    createLabel && createFields && createFields.length > 0,
  );

  const create = useApiMutation<Record<string, string>, unknown>(
    queryKey,
    (input) => {
      const body = toBody
        ? toBody(input)
        : Object.fromEntries(
            Object.entries(input).filter(([, value]) => value !== ""),
          );
      // The dialog's key, made when it opened (openCreate).
      return post(
        createPath ?? path,
        body,
        idempotencyKey.current ?? undefined,
      );
    },
  );

  const openCreate = () => {
    setForm(
      Object.fromEntries(
        (createFields ?? []).map((f) => {
          const first = f.options?.[0];
          return [
            f.field,
            f.initial ??
              (f.emptyOption !== undefined || first === undefined
                ? ""
                : optionValue(first)),
          ];
        }),
      ),
    );
    idempotencyKey.current = idempotent ? crypto.randomUUID() : null;
    setCreating(true);
  };

  const rows = data?.items ?? [];

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        actions={
          canCreate ? (
            <Button
              variant="primary"
              onClick={() => (creating ? setCreating(false) : openCreate())}
            >
              {creating ? t("common.cancel") : createLabel}
            </Button>
          ) : undefined
        }
      />

      {children}

      {creating && canCreate && (
        <Card title={createLabel} className="mb-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate(form, {
                onSuccess: () => {
                  idempotencyKey.current = null;
                  setCreating(false);
                  setForm({});
                },
              });
            }}
          >
            <div className="atlas-grid atlas-grid--3">
              {(createFields ?? []).map((f) => (
                <Field
                  key={f.field}
                  label={f.label}
                  required={f.required}
                  hint={f.hint}
                >
                  {f.options ? (
                    <Select
                      value={form[f.field] ?? ""}
                      required={f.required}
                      onChange={(e) =>
                        setForm({ ...form, [f.field]: e.target.value })
                      }
                    >
                      {f.emptyOption !== undefined && (
                        <option value="">{f.emptyOption}</option>
                      )}
                      {f.options.map((option) => (
                        <option
                          key={optionValue(option)}
                          value={optionValue(option)}
                        >
                          {optionLabel(option)}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={f.type ?? "text"}
                      value={form[f.field] ?? ""}
                      required={f.required}
                      placeholder={f.placeholder}
                      onChange={(e) =>
                        setForm({ ...form, [f.field]: e.target.value })
                      }
                    />
                  )}
                </Field>
              ))}
            </div>
            {create.error && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlock: 8,
                }}
              >
                <ErrorText error={create.error} />
              </div>
            )}
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Skeleton rows={8} />
      ) : error ? (
        // The reason goes where EmptyState puts its action: it can carry a
        // Details disclosure, which a <p> message cannot hold.
        <EmptyState
          title={t("resource.loadFailed", { title })}
          action={
            <div role="alert" style={{ fontSize: 14 }}>
              <ErrorText error={error} />
            </div>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          message={emptyMessage}
          action={
            canCreate ? (
              <Button variant="primary" onClick={openCreate}>
                {createLabel}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card title={listTitle(title, data?.total ?? rows.length)}>
          <DataTable<TRow> rows={rows} columns={columns} />
        </Card>
      )}
    </div>
  );
}
